import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS, buildApiUrl, API_CONFIG, useDjangoAPI } from '../config/api';

console.log('AuthService using API:', useDjangoAPI ? 'Django Backend' : 'Express Backend');
console.log('API Base URL:', API_BASE_URL);

// DEV MODE: Bypass authentication for development without backend
const DEV_MODE = process.env.REACT_APP_DEV_MODE === 'true' || false; // Set to true to enable mock auth
console.log('🔧 DEV MODE:', DEV_MODE ? 'ENABLED (Mock Auth)' : 'DISABLED (Real Auth)');

class AuthService {
  async login(email, password) {
    // DEV MODE: Mock successful login
    if (DEV_MODE) {
      console.log('🔧 DEV MODE: Bypassing authentication');
      const mockUser = {
        id: '00000000-0000-0000-0000-000000000000',
        email: email || 'dev@olymp.local',
        name: 'Dev User',
        has_hermes_access: true,
        has_olymp_access: true
      };
      const mockToken = 'dev-mock-token-' + Date.now();

      localStorage.setItem('authToken', mockToken);
      localStorage.setItem('user', JSON.stringify(mockUser));
      console.log('✅ Mock login successful:', mockUser);

      return { success: true, user: mockUser };
    }

    try {
      const loginUrl = buildApiUrl(API_ENDPOINTS.AUTH.LOGIN);
      console.log('=== LOGIN DEBUG ===');
      console.log('Login attempt to:', loginUrl);
      console.log('Login credentials:', { email, password: '***' });
      console.log('==================');

      const response = await axios.post(loginUrl, {
        email,
        password
      }, API_CONFIG);

      console.log('Login response:', response.data);

      // ARGUS Auth Service returns { access, refresh, user }
      if (response.data.access && response.data.user) {
        // Store access token (JWT)
        localStorage.setItem('authToken', response.data.access);

        // Store refresh token
        if (response.data.refresh) {
          localStorage.setItem('refreshToken', response.data.refresh);
        }

        // Store user data
        localStorage.setItem('user', JSON.stringify(response.data.user));

        console.log('✅ Login successful:', response.data.user);
        return { success: true, user: response.data.user };
      }

      return { success: false, message: 'Keine gültige Antwort vom Server' };
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error details:', error.response?.data || error.message);

      // Detaillierte Fehlermeldung
      let errorMessage = 'Ein Fehler ist aufgetreten';
      if (error.response) {
        // Der Server hat mit einem Fehlerstatuscode geantwortet
        const serverError = error.response.data?.detail || error.response.data?.message || error.response.statusText;
        errorMessage = `Login fehlgeschlagen: ${serverError}`;
      } else if (error.request) {
        // Die Anfrage wurde gestellt, aber keine Antwort erhalten
        errorMessage = 'Keine Antwort vom Server. Bitte überprüfen Sie Ihre Internetverbindung.';
      } else {
        // Etwas ist beim Einrichten der Anfrage schief gelaufen
        errorMessage = `Fehler bei der Anfrage: ${error.message}`;
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }
  
  logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }
  
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    return JSON.parse(userStr);
  }
  
  getToken() {
    return localStorage.getItem('authToken');
  }
  
  async isLoggedIn() {
    const token = this.getToken();
    if (!token) return false;

    // DEV MODE: Skip token validation
    if (DEV_MODE) {
      console.log('🔧 DEV MODE: Skipping token validation');
      return true;
    }

    try {
      const validateUrl = buildApiUrl(API_ENDPOINTS.AUTH.VALIDATE_TOKEN);

      // ARGUS Auth Service: GET request with Bearer token in header
      const response = await axios.get(validateUrl, {
        ...API_CONFIG,
        headers: {
          ...API_CONFIG.headers,
          'Authorization': `Bearer ${token}`
        }
      });

      return response.data.valid === true && response.status === 200;
    } catch (error) {
      console.log('Token validation failed:', error.response?.status);
      // Nur bei expliziten 401/403 Fehlern ausloggen, nicht bei Netzwerkfehlern
      if (error.response?.status === 401 || error.response?.status === 403) {
        this.logout(); // Token ungültig, ausloggen
      }
      return false;
    }
  }
  
  async refreshAccessToken() {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;
    try {
      const response = await axios.post(
        buildApiUrl(API_ENDPOINTS.AUTH.REFRESH),
        { refresh: refreshToken },
        { ...API_CONFIG, _skipRefreshInterceptor: true }
      );
      const newAccess = response.data.access;
      if (newAccess) {
        localStorage.setItem('authToken', newAccess);
        return newAccess;
      }
      return null;
    } catch {
      this.logout();
      return null;
    }
  }

  // Setup Axios Interceptor für automatisches Token-Handling
  setupAxiosInterceptors() {
    axios.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response?.status === 401
          && !originalRequest._skipRefreshInterceptor
          && !originalRequest._retried
        ) {
          originalRequest._retried = true;
          const newToken = await this.refreshAccessToken();
          if (newToken) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(originalRequest);
          }
        }
        return Promise.reject(error);
      }
    );
  }
}

const authService = new AuthService();
authService.setupAxiosInterceptors();
export default authService;
